/*
 * Word lookups for the Interactive Text definition popover.
 *
 * Backed by api.dictionaryapi.dev: free, CORS-open, unversioned and unmetered.
 * Unmetered means unpoliced, not free of consequence — the transcript invites
 * repeat taps on the same word, so every answer is cached for the life of the
 * tab, the misses included.
 *
 * Deliberately a bare `fetch` rather than `apiClient`. `apiClient` attaches the
 * viewer's Bearer JWT to everything it sends, and this is a third-party host;
 * the same rule the ContentPage follows for presigned caption URLs.
 */

export type Definition = {
  word: string;
  /** verb, noun, adjective… shown as the green pill. */
  partOfSpeech: string;
  /** IPA, e.g. /ˈlɪŋ.ɡəd/. */
  pronunciation?: string;
  definition: string;
  examples: string[];
  /** Pronunciation audio. Not every entry has one; the speaker button disables. */
  audioUrl?: string;
};

const ENDPOINT = 'https://api.dictionaryapi.dev/api/v2/entries/en/';

/** Shortest token worth offering a lookup on — "a", "of" and "to" are noise. */
const MIN_LENGTH = 3;

const LETTERS_ONLY = /^\p{L}+$/u;

/** How many of a sense's usage examples the popover has room for. */
const MAX_EXAMPLES = 3;

/*
 * Settled answers, keyed by normalized word. A `Map` rather than an object on
 * purpose: the stub this replaced kept entries on a plain object, and `'constructor'
 * in entries` was true, so the word "constructor" in a transcript came back as
 * `Object` itself — truthy, so it survived the `?? null` — and the popover
 * rendered a definition of `undefined`. A Map has no prototype to walk into.
 */
const settled = new Map<string, Definition | null>();

/** Lookups still in the air, so a double-tap makes one request, not two. */
const inFlight = new Map<string, Promise<Definition | null>>();

/** Strips surrounding punctuation and case so "lingered," matches "lingered". */
export function normalizeWord(raw: string): string {
  return raw.toLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '');
}

/*
 * A shape test, not a membership test. This runs once per word per render of the
 * transcript (InteractiveText), so it has to answer synchronously, and a real
 * dictionary behind a network call cannot. It asks only "could this token be a
 * word", which is all the transcript needs in order to decide what is tappable —
 * and against a general dictionary the answer for an ordinary word is yes anyway.
 *
 * Contractions and hyphenated compounds fall out here rather than being special-
 * cased: "don't" and "well-trodden" are not headwords, so a lookup would spend a
 * request to display "no definition found".
 */
export function canLookUp(raw: string): boolean {
  const word = normalizeWord(raw);
  return word.length >= MIN_LENGTH && LETTERS_ONLY.test(word);
}

/**
 * Resolves to the entry, or to `null` when the dictionary has no such word.
 * Rejects when the dictionary could not be reached — the caller has to tell a
 * viewer who is offline from one who typed a word nobody has defined.
 */
export function lookUp(raw: string): Promise<Definition | null> {
  const word = normalizeWord(raw);
  if (!canLookUp(word)) return Promise.resolve(null);

  const cached = settled.get(word);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = inFlight.get(word);
  if (pending) return pending;

  // Only a settled answer is cached. A failed request is about the network, not
  // about the word, so the next tap tries again.
  const request = fetchEntry(word)
    .then((entry) => {
      settled.set(word, entry);
      return entry;
    })
    .finally(() => {
      inFlight.delete(word);
    });

  inFlight.set(word, request);
  return request;
}

/*
 * One retry, and it earns its place. Measured against the live service, the same
 * word returns 200 and then 502 seconds apart, and sometimes fails at the
 * connection level entirely — free, unmetered and unreliable in roughly equal
 * measure. Without a retry the popover shows an error on a healthy word often
 * enough to read as broken.
 *
 * Only transport failures and 5xx are retried. A 404 is an answer, and a 4xx is
 * about the request, so repeating either just spends another round trip.
 */
const RETRY_DELAY_MS = 400;

async function fetchEntry(word: string): Promise<Definition | null> {
  const url = `${ENDPOINT}${encodeURIComponent(word)}`;

  // No `Accept` header: it is a third-party host and any header beyond the CORS
  // safelist turns a simple GET into a preflighted one, which is a round trip
  // and an OPTIONS handler this API need not have. It serves JSON regardless.
  let response: Response;
  try {
    response = await fetch(url);
    if (response.status >= 500) throw new Error(`upstream ${response.status}`);
  } catch {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    response = await fetch(url);
  }

  // 404 is how this API says "no such word" — an answer, and a cacheable one.
  // Every other failing status is the service or the network, and throws.
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Dictionary lookup failed (${response.status})`);

  return toDefinition(word, (await response.json()) as unknown);
}

type ApiPhonetic = { text?: string | null; audio?: string | null };
type ApiSense = { definition?: string | null; example?: string | null };
type ApiMeaning = { partOfSpeech?: string | null; definitions?: ApiSense[] | null };
type ApiEntry = {
  word?: string | null;
  phonetic?: string | null;
  phonetics?: ApiPhonetic[] | null;
  meanings?: ApiMeaning[] | null;
};

/*
 * The API returns every entry for the spelling, each with several parts of
 * speech, each with several senses — far more than a popover beside a video can
 * show. Take the first sense that carries actual text, which is the API's own
 * ordering and so the most common reading of the word.
 */
function toDefinition(word: string, payload: unknown): Definition | null {
  if (!Array.isArray(payload)) return null;

  for (const entry of payload as ApiEntry[]) {
    for (const meaning of entry.meanings ?? []) {
      const senses = (meaning.definitions ?? [])
        .map((sense) => ({
          text: sense.definition?.trim() ?? '',
          example: sense.example?.trim() ?? '',
        }))
        .filter((sense) => sense.text !== '');

      if (senses.length === 0) continue;

      return {
        word: entry.word?.trim() || word,
        partOfSpeech: meaning.partOfSpeech?.trim() || 'word',
        pronunciation: pickPronunciation(entry),
        definition: senses[0].text,
        examples: senses
          .map((sense) => sense.example)
          .filter((example) => example !== '')
          .slice(0, MAX_EXAMPLES),
        audioUrl: pickAudio(entry),
      };
    }
  }

  return null;
}

/* IPA comes back bare from some entries ("həˈləʊ") and slashed from others
   ("/həˈləʊ/"). The popover renders it verbatim, so settle it here. */
function pickPronunciation(entry: ApiEntry): string | undefined {
  const raw = entry.phonetic?.trim() || entry.phonetics?.find((p) => p.text?.trim())?.text?.trim();
  const bare = raw?.replace(/^\/+|\/+$/g, '');
  return bare ? `/${bare}/` : undefined;
}

/* Most entries list several pronunciations and only some carry audio, so take
   the first that does. Those URLs are absolute https today, but this API has
   served them protocol-relative ("//ssl.gstatic.com/…") and carries no version
   in its path, so pin the scheme rather than trust it: the popover hands the
   value straight to `new Audio()`, and a cached entry should not resolve
   differently depending on how the page itself was served. */
function pickAudio(entry: ApiEntry): string | undefined {
  const audio = entry.phonetics?.find((p) => p.audio?.trim())?.audio?.trim();
  if (!audio) return undefined;
  return audio.startsWith('//') ? `https:${audio}` : audio;
}
